/**
 * Crumb label context — the seam that lets a page name the dynamic tail of the
 * top-nav breadcrumb. The trail is derived purely from the pathname
 * (`deriveNavTrail` → `deriveBreadcrumbTrail`), so a dynamic detail route
 * (`/settings/integrations/connections/2`, `/settings/repos/5`) can only ever
 * read as its raw id — the chrome has no way to know the entity's display name.
 *
 * It mirrors `rail-extension-context.tsx` / `mode-subnav-context.tsx`: a page
 * deep in `children` registers `{href → label}` overrides via
 * `useProvideCrumbLabel` (or the tiny render-null `<CrumbLabel>` registrar, so a
 * server page can register without becoming a client component), and the
 * layout-level `NavBreadcrumb` reads them with `useCrumbLabels`, re-labelling
 * any crumb whose href matches. Keyed by EXACT href, so a stale registration
 * from the outgoing page during the navigation window can never mislabel the
 * incoming page's trail (its hrefs don't match). Clears on unmount.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const EMPTY_LABELS: Record<string, string> = {};

interface Ctx {
  labels: Record<string, string>;
  setLabel: (href: string, label: string | null) => void;
}

const CrumbLabelContext = createContext<Ctx | null>(null);

export function CrumbLabelProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<Record<string, string>>(EMPTY_LABELS);
  // Idempotent setter (same-value sets return the previous object) so a caller
  // that re-registers every render can't trigger a set→render→set loop.
  const setLabel = useCallback((href: string, label: string | null) => {
    setLabels((prev) => {
      if (label === null) {
        if (!(href in prev)) return prev;
        const next = { ...prev };
        delete next[href];
        return next;
      }
      if (prev[href] === label) return prev;
      return { ...prev, [href]: label };
    });
  }, []);
  const value = useMemo(() => ({ labels, setLabel }), [labels, setLabel]);
  return (
    <CrumbLabelContext.Provider value={value}>
      {children}
    </CrumbLabelContext.Provider>
  );
}

/** The live `{href → label}` overrides (empty outside the provider). */
export function useCrumbLabels(): Record<string, string> {
  const ctx = useContext(CrumbLabelContext);
  return ctx?.labels ?? EMPTY_LABELS;
}

/** Register a breadcrumb label for `href` while this component is mounted;
 *  clears on unmount. A nullish label is a no-op (data still loading). */
export function useProvideCrumbLabel(
  href: string,
  label: string | null | undefined,
): void {
  const ctx = useContext(CrumbLabelContext);
  const set = ctx?.setLabel;

  useEffect(() => {
    if (!set || !label) return;
    set(href, label);
    return () => set(href, null);
  }, [set, href, label]);
}

/** Render-null registrar so a SERVER page can name its own crumb:
 *  `<CrumbLabel href={pathname} label={entity.display_name} />`. */
export function CrumbLabel({ href, label }: { href: string; label: string }) {
  useProvideCrumbLabel(href, label);
  return null;
}
